import React, { useState } from 'react';
import { X, ArrowUpRight, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActionDialog = ({ open, onClose, title, eyebrow, children, footer, maxWidth = 'max-w-lg' }) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-[#0f2a1e]/50 backdrop-blur-sm" />
      <div className={`relative bg-[#f5f1e6] rounded-2xl w-full ${maxWidth} max-h-[90vh] overflow-hidden border border-[#143324]/10 shadow-2xl flex flex-col`}>
        <div className="flex items-start justify-between px-6 pt-5 pb-3">
          <div>
            {eyebrow && (
              <p className="text-[10.5px] tracking-[0.2em] uppercase text-[#143324] font-body mb-1">{eyebrow}</p>
            )}
            <h3 className="font-serif-display text-[#143324] text-2xl md:text-3xl leading-tight">{title}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#143324]/8 flex items-center justify-center text-[#143324] mt-1" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 pb-6 overflow-y-auto">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-[#143324]/10">{footer}</div>}
      </div>
    </div>
  );
};

const formInput = 'w-full px-4 py-2.5 rounded-lg border border-[#143324]/15 bg-white text-[14px] font-body text-[#1c1c1c] outline-none focus:border-[#143324]/40';
const formLabel = 'block text-[11.5px] tracking-wider uppercase text-[#3d3d3d] font-body mb-1.5';

const SuccessMessage = ({ title, subtitle, onClose }) => (
  <div className="py-8 text-center">
    <div className="w-14 h-14 rounded-full bg-[#143324] text-[#f5f1e6] flex items-center justify-center mx-auto mb-4">
      <CheckCircle2 size={26} />
    </div>
    <h4 className="font-serif-display text-[#143324] text-2xl mb-2">{title}</h4>
    <p className="text-[#3d3d3d] font-body text-[14px] mb-6">{subtitle}</p>
    <button onClick={onClose} className="btn-primary px-6 py-2.5 rounded-full text-[13px] font-body">Close</button>
  </div>
);

const submitLead = (payload) => axios.post(`${API}/leads`, payload);

// ---------- Trial Class ----------
export const TrialClassDialog = ({ open, onClose }) => {
  const [f, setF] = useState({ name: '', email: '', phone: '', date: '', slot: '', course: 'EA Course' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitLead({ kind: 'trial', ...f }); setDone(true); } catch (_) {}
    setLoading(false);
  };
  const close = () => { setDone(false); setF({ name: '', email: '', phone: '', date: '', slot: '', course: 'EA Course' }); onClose(); };

  return (
    <ActionDialog open={open} onClose={close} eyebrow="Free Trial Class" title="Book your live trial session">
      {done ? (
        <SuccessMessage title="Trial booked!" subtitle="Our counselor will confirm your slot via WhatsApp within 30 minutes." onClose={close} />
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formLabel}>Full name*</label>
              <input required name="name" value={f.name} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Email*</label>
              <input required type="email" name="email" value={f.email} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Phone*</label>
              <input required name="phone" value={f.phone} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Course</label>
              <select name="course" value={f.course} onChange={handle} className={formInput}>
                <option>EA Course</option>
                <option>US Taxation</option>
                <option>Business Setup</option>
              </select>
            </div>
            <div>
              <label className={formLabel}>Preferred date*</label>
              <input required type="date" name="date" value={f.date} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Preferred time*</label>
              <select required name="slot" value={f.slot} onChange={handle} className={formInput}>
                <option value="">Choose a slot</option>
                <option>10:00 AM – 11:00 AM</option>
                <option>12:00 PM – 1:00 PM</option>
                <option>4:00 PM – 5:00 PM</option>
                <option>7:00 PM – 8:00 PM</option>
              </select>
            </div>
          </div>
          <button disabled={loading} type="submit" className="w-full mt-3 btn-primary px-5 py-3 rounded-lg text-[14px] font-body inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Confirm booking <ArrowUpRight size={14} /></>}
          </button>
          <p className="text-[11px] text-center text-[#3d3d3d] font-body">Live 45-min session with a certified EA. No credit card required.</p>
        </form>
      )}
    </ActionDialog>
  );
};

// ---------- Exam Register ----------
export const ExamRegisterDialog = ({ open, onClose }) => {
  const [f, setF] = useState({ name: '', email: '', phone: '', course: 'Part 1 – Individuals' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitLead({ kind: 'exam', ...f, message: 'SEE exam registration assistance' }); setDone(true); } catch (_) {}
    setLoading(false);
  };
  const close = () => { setDone(false); onClose(); };

  return (
    <ActionDialog open={open} onClose={close} eyebrow="SEE Exam" title="Register for your SEE exam">
      {done ? (
        <SuccessMessage title="Request received" subtitle="We’ll guide you through Prometric scheduling within 24 hours." onClose={close} />
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-[13px] text-[#3d3d3d] font-body -mt-2">
            Our team will help you schedule your SEE exam through Prometric, prepare all documents, and coach you the week before your test.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formLabel}>Full name*</label>
              <input required name="name" value={f.name} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Email*</label>
              <input required type="email" name="email" value={f.email} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Phone*</label>
              <input required name="phone" value={f.phone} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Which part?</label>
              <select name="course" value={f.course} onChange={handle} className={formInput}>
                <option>Part 1 – Individuals</option>
                <option>Part 2 – Businesses</option>
                <option>Part 3 – Representation</option>
                <option>All 3 parts</option>
              </select>
            </div>
          </div>
          <button disabled={loading} type="submit" className="w-full mt-2 btn-primary px-5 py-3 rounded-lg text-[14px] font-body inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Request registration help <ArrowUpRight size={14} /></>}
          </button>
        </form>
      )}
    </ActionDialog>
  );
};

// ---------- PTIN Help ----------
export const PtinHelpDialog = ({ open, onClose }) => {
  const [f, setF] = useState({ name: '', email: '', phone: '', status: 'New PTIN application', ssn_last4: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitLead({ kind: 'ptin', ...f, message: 'PTIN application assistance' }); setDone(true); } catch (_) {}
    setLoading(false);
  };
  const close = () => { setDone(false); onClose(); };

  return (
    <ActionDialog open={open} onClose={close} eyebrow="IRS PTIN" title="Get help with your PTIN application">
      {done ? (
        <SuccessMessage title="Request received" subtitle="Our team will walk you through the IRS PTIN application within 24 hours." onClose={close} />
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-[13px] text-[#3d3d3d] font-body -mt-2">
            A Preparer Tax ID Number (PTIN) is required by the IRS to prepare US tax returns for compensation. We’ll help you file the application, upload documents, and pay the $19.75 fee — usually done in one 30-minute session.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formLabel}>Full name*</label>
              <input required name="name" value={f.name} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Email*</label>
              <input required type="email" name="email" value={f.email} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Phone*</label>
              <input required name="phone" value={f.phone} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>What do you need?</label>
              <select name="status" value={f.status} onChange={handle} className={formInput}>
                <option>New PTIN application</option>
                <option>Annual PTIN renewal</option>
                <option>ITIN application (no SSN)</option>
                <option>General PTIN guidance</option>
              </select>
            </div>
          </div>
          <button disabled={loading} type="submit" className="w-full mt-2 btn-primary px-5 py-3 rounded-lg text-[14px] font-body inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Request PTIN help <ArrowUpRight size={14} /></>}
          </button>
          <p className="text-[11px] text-center text-[#3d3d3d] font-body">Includes free ITIN guidance for candidates without a US SSN.</p>
        </form>
      )}
    </ActionDialog>
  );
};

// ---------- Meeting Dialog ----------
export const MeetingDialog = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState({ date: '', slot: '' });
  const [f, setF] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));

  // Simple mini calendar: next 14 days
  const days = Array.from({ length: 14 }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });
  const slots = ['10:00 AM', '11:30 AM', '2:00 PM', '3:30 PM', '5:00 PM', '6:30 PM'];
  const monthFmt = new Intl.DateTimeFormat('en', { month: 'short' });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({
        kind: 'meeting',
        ...f,
        date: selected.date,
        slot: selected.slot,
        message: (f.message || '') + '\nZoho meeting link: <your-zoho-url>',
      });
      setDone(true);
    } catch (_) {}
    setLoading(false);
  };

  const close = () => { setDone(false); setStep(1); setSelected({ date: '', slot: '' }); setF({ name: '', email: '', phone: '', message: '' }); onClose(); };

  return (
    <ActionDialog open={open} onClose={close} eyebrow="Book a Meeting" title="Schedule with our counselor" maxWidth="max-w-2xl">
      {done ? (
        <SuccessMessage
          title="Meeting scheduled!"
          subtitle={`We’ll email you a Zoho meeting link for ${selected.date} at ${selected.slot}.`}
          onClose={close}
        />
      ) : step === 1 ? (
        <div>
          <p className="text-[13px] text-[#3d3d3d] font-body mb-4">
            Pick a date and time. You’ll get a Zoho Meeting invite by email.
          </p>
          <p className={formLabel + ' mb-3'}>Select a date</p>
          <div className="grid grid-cols-7 gap-2 mb-6">
            {days.map((d) => {
              const key = d.toISOString().slice(0, 10);
              const isSel = selected.date === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelected((p) => ({ ...p, date: key }))}
                  className={`flex flex-col items-center py-2.5 rounded-xl border transition-all ${
                    isSel
                      ? 'bg-[#143324] text-[#f5f1e6] border-[#143324]'
                      : 'bg-white border-[#143324]/12 text-[#1c1c1c] hover:border-[#143324]/40'
                  }`}
                >
                  <span className={`text-[10px] font-body uppercase ${isSel ? 'text-[#c9c2a8]' : 'text-[#3d3d3d]'}`}>
                    {d.toLocaleDateString('en', { weekday: 'short' }).slice(0, 3)}
                  </span>
                  <span className="font-serif-display text-lg leading-none mt-0.5">{d.getDate()}</span>
                  <span className={`text-[9.5px] font-body ${isSel ? 'text-[#c9c2a8]' : 'text-[#3d3d3d]'}`}>
                    {monthFmt.format(d)}
                  </span>
                </button>
              );
            })}
          </div>
          {selected.date && (
            <>
              <p className={formLabel + ' mb-3'}>Select a time</p>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {slots.map((s) => {
                  const isSel = selected.slot === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setSelected((p) => ({ ...p, slot: s }))}
                      className={`py-2.5 rounded-xl border text-[13px] font-body transition-colors ${
                        isSel ? 'bg-[#143324] text-[#f5f1e6] border-[#143324]' : 'bg-white border-[#143324]/12 text-[#1c1c1c] hover:border-[#143324]/40'
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          <button
            disabled={!selected.date || !selected.slot}
            onClick={() => setStep(2)}
            className="w-full btn-primary px-5 py-3 rounded-lg text-[14px] font-body inline-flex items-center justify-center gap-2 disabled:opacity-40"
          >
            Next <ArrowUpRight size={14} />
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-[13px] text-[#3d3d3d] font-body -mt-2">
            You selected <span className="text-[#143324] font-medium">{selected.date}</span> at <span className="text-[#143324] font-medium">{selected.slot}</span>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formLabel}>Full name*</label>
              <input required name="name" value={f.name} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Email*</label>
              <input required type="email" name="email" value={f.email} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Phone</label>
              <input name="phone" value={f.phone} onChange={handle} className={formInput} />
            </div>
          </div>
          <div>
            <label className={formLabel}>What would you like to discuss?</label>
            <textarea name="message" value={f.message} onChange={handle} rows={3} className={formInput} placeholder="e.g., EA course fees, exam schedule, placement opportunities…" />
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setStep(1)} className="btn-outline px-5 py-2.5 rounded-lg text-[13px] font-body">Back</button>
            <button disabled={loading} type="submit" className="flex-1 btn-primary px-5 py-3 rounded-lg text-[14px] font-body inline-flex items-center justify-center gap-2">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <>Confirm meeting <ArrowUpRight size={14} /></>}
            </button>
          </div>
        </form>
      )}
    </ActionDialog>
  );
};

// ---------- Refer a Friend ----------
export const ReferFriendDialog = ({ open, onClose }) => {
  const [f, setF] = useState({ referrer_name: '', email: '', friend_name: '', friend_email: '', friend_phone: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitLead({ kind: 'refer', ...f }); setDone(true); } catch (_) {}
    setLoading(false);
  };
  const close = () => { setDone(false); onClose(); };

  return (
    <ActionDialog open={open} onClose={close} eyebrow="Refer & Earn" title="Refer a friend, earn ₹5,000">
      {done ? (
        <SuccessMessage title="Referral sent!" subtitle="You’ll receive ₹5,000 when your friend enrolls." onClose={close} />
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-[13px] text-[#3d3d3d] font-body -mt-2">
            Every friend who enrolls through you earns them ₹5,000 off, and you ₹5,000 in cash.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formLabel}>Your name*</label>
              <input required name="referrer_name" value={f.referrer_name} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Your email*</label>
              <input required type="email" name="email" value={f.email} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Friend’s name*</label>
              <input required name="friend_name" value={f.friend_name} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Friend’s email*</label>
              <input required type="email" name="friend_email" value={f.friend_email} onChange={handle} className={formInput} />
            </div>
            <div className="md:col-span-2">
              <label className={formLabel}>Friend’s phone</label>
              <input name="friend_phone" value={f.friend_phone} onChange={handle} className={formInput} />
            </div>
          </div>
          <button disabled={loading} type="submit" className="w-full mt-2 btn-primary px-5 py-3 rounded-lg text-[14px] font-body inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Send referral <ArrowUpRight size={14} /></>}
          </button>
        </form>
      )}
    </ActionDialog>
  );
};

// ---------- Brochure Download ----------
export const BrochureDialog = ({ open, onClose }) => {
  const [f, setF] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitLead({ kind: 'brochure', ...f }); setDone(true); } catch (_) {}
    setLoading(false);
  };
  const close = () => { setDone(false); onClose(); };

  return (
    <ActionDialog open={open} onClose={close} eyebrow="Course Brochure" title="Get the full curriculum PDF">
      {done ? (
        <SuccessMessage title="Sent!" subtitle="Check your email for the brochure PDF (also check your spam folder)." onClose={close} />
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-[13px] text-[#3d3d3d] font-body -mt-2">
            We’ll email you a detailed brochure covering syllabus, fees, placement stats, and instructor profiles.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={formLabel}>Full name*</label>
              <input required name="name" value={f.name} onChange={handle} className={formInput} />
            </div>
            <div>
              <label className={formLabel}>Email*</label>
              <input required type="email" name="email" value={f.email} onChange={handle} className={formInput} />
            </div>
            <div className="md:col-span-2">
              <label className={formLabel}>Phone</label>
              <input name="phone" value={f.phone} onChange={handle} className={formInput} />
            </div>
          </div>
          <button disabled={loading} type="submit" className="w-full mt-2 btn-primary px-5 py-3 rounded-lg text-[14px] font-body inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Email me the brochure <ArrowUpRight size={14} /></>}
          </button>
        </form>
      )}
    </ActionDialog>
  );
};
